import React from 'react';
import {
  Body,
  Icon,
  Tooltip,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import styles from './ListTagsCell.module.scss';

const MAX_VISIBLE_TAGS = 2;

interface ListTag {
  name: string;
  color?: string;
}

interface ListTagsCellProps {
  lists: ListTag[];
  /**
   * When true, prepends a small heart icon before the list tags as a quiet
   * shortlist indicator. Rendered even when `lists` is empty so the icon
   * still shows for creators with no list memberships.
   */
  isShortlisted?: boolean;
}

function ListTagsCell({lists, isShortlisted = false}: ListTagsCellProps) {
  if (lists.length === 0 && !isShortlisted) {
    return null;
  }

  const defaultBorderColor = designSystemToken('reference.gray.200');
  const visibleTags = lists.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = lists.length - MAX_VISIBLE_TAGS;

  return (
    <div className={styles.container}>
      {isShortlisted ? (
        <Tooltip title={translate('Shortlisted')} placement="top" arrow>
          {/* Passive indicator — not a control. We stop click propagation
              so tapping the heart doesn't accidentally trigger the row's
              onClick (which opens the side panel) and read as a confusing
              destructive action. Toggling shortlist on/off happens via the
              row menu or bulk actions, which are the canonical paths. */}
          <span
            className={styles.shortlistedIcon}
            data-testid="shortlisted-icon"
            role="img"
            aria-label={translate('Shortlisted')}
          >
            <Icon
              size="small"
              appearance="neutral"
              name="Navigation-Heart-Fill"
              color={designSystemToken('semantic.fg.tertiary')}
            />
          </span>
        </Tooltip>
      ) : null}
      {visibleTags.map((tag) => (
        <div
          key={tag.name}
          className={styles.tag}
          style={{borderColor: tag.color ?? defaultBorderColor}}
        >
          <Body size="md">{tag.name}</Body>
        </div>
      ))}
      {overflowCount > 0 ? (
        <div className={styles.tag} style={{borderColor: defaultBorderColor}}>
          <Body size="md">+{overflowCount}</Body>
        </div>
      ) : null}
    </div>
  );
}

export default ListTagsCell;
